import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { ArquivoDaFicha, CanalDeIntegracao } from '@/data/tipos';

/**
 * Manda um anexo da ficha para o WhatsApp do próprio paciente — trilha T6.
 *
 * Sai do prontuário um arquivo com dado de saúde: por isso há sempre um passo
 * de confirmação com o arquivo e o canal à vista, e só por WhatsApp CONECTADO
 * (o envio de mídia da trilha T4). Sem canal conectado, o botão diz o motivo em
 * vez de enfileirar um arquivo que não tem por onde sair.
 *
 * Documento emitido não tem arquivo guardado — ele é impresso da tela —, então
 * o que se envia por aqui é o anexo (imagem ou PDF) que foi guardado na ficha.
 */
export function EnviarAnexoPeloWhatsApp({ contatoId, arquivo }: { contatoId: string; arquivo: ArquivoDaFicha }) {
    const aviso = useAviso();
    const navigate = useNavigate();
    const [aberto, setAberto] = useState(false);
    const [canais, setCanais] = useState<CanalDeIntegracao[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [enviando, setEnviando] = useState<string | null>(null);

    const abrir = async () => {
        setAberto(true);
        setErro(null);
        setCanais(null);
        try {
            const todos = await dados().canaisDaClinica();
            setCanais(todos.filter((c) => c.tipo === 'whatsapp' && c.status === 'conectado'));
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler os canais.');
        }
    };

    const enviar = async (canal: CanalDeIntegracao) => {
        setEnviando(canal.id);
        setErro(null);
        try {
            const conversa = await dados().abrirConversaCom(contatoId, canal.id);
            const endereco = await dados().enderecoDoAnexo(arquivo.caminho);
            const resposta = await fetch(endereco);
            if (!resposta.ok) throw new Error('O anexo não pôde ser lido do cofre agora. Tente de novo.');
            const conteudo = await resposta.blob();
            await dados().enviarArquivo(conversa, new File([conteudo], arquivo.nome, { type: arquivo.tipo }), arquivo.nome);
            setAberto(false);
            aviso({ title: 'Anexo enviado pelo WhatsApp', description: arquivo.nome });
            navigate(`/app/comunicacao?conversa=${conversa}`);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível enviar o anexo.');
        } finally {
            setEnviando(null);
        }
    };

    return (
        <>
            <Button
                size="sm" variant="ghost" iconLeft={<Icon name="send" size={14} />}
                onClick={() => { void abrir(); }} aria-label={`Enviar ${arquivo.nome} pelo WhatsApp`}
            >
                Enviar
            </Button>
            <Modal
                open={aberto} onClose={() => setAberto(false)} width={460} title="Enviar anexo pelo WhatsApp"
                description={`${arquivo.nome} sai do prontuário para o WhatsApp do paciente. Confira o número na conversa.`}
                footer={<Button variant="secondary" onClick={() => setAberto(false)}>Cancelar</Button>}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                    {canais === null && !erro && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Vendo quais canais estão no ar…</p>}
                    {canais !== null && canais.length === 0 && (
                        <Alert tone="warning" dismissible={false} title="Nenhum WhatsApp conectado">
                            Arquivo só sai por WhatsApp conectado. Conecte um canal em Configurações › Integrações.
                        </Alert>
                    )}
                    {(canais ?? []).map((canal) => (
                        <Button
                            key={canal.id} variant="secondary" loading={enviando === canal.id} disabled={enviando !== null}
                            iconLeft={<Icon name="message-circle" size={14} />} onClick={() => { void enviar(canal); }}
                        >
                            {`Enviar por ${canal.nome}`}
                        </Button>
                    ))}
                </div>
            </Modal>
        </>
    );
}
